import gql from 'graphql-tag';

import {
  BypassAmountPageEnrolmentQuery,
  PageType,
} from '../../types/__generated__';
import { PageMetaContext } from '../context';
import { useNotNullableContext } from './useNotNullableContext';
import { filterByTypename } from '../utils/graphql';
import isMobile from '../utils/isMobile';
import { useEnrolUser } from '@justgiving/experiments';
import { Experiments } from '../constants/experiments';
import { Variation } from '../utils/abTests';
import useFullPageQuery from './useFullPageQuery';
import { useAuthenticateUser } from './useAuthenticateUser';

// NOTE: using a full query and the `isFundraisingPage` flag so that the countryCode is only requested for FR pages.
// This field is resolved using the consumer API, so it's slow.
const DOCUMENT = gql`
  query BypassAmountPageEnrolment(
    $type: PageType!
    $slug: Slug!
    $isFundraisingPage: Boolean!
  ) {
    page(type: $type, slug: $slug) {
      id
      donationSummary {
        totalAmount {
          currencyCode
        }
      }
      relationships {
        beneficiaries(first: 1) {
          totalCount
          nodes {
            ... on Charity {
              countryCode @include(if: $isFundraisingPage)
            }
          }
        }
      }
    }
  }
`;

// AB test: starting_donation_on_frp_with_amount_page
// Can be removed when experiment has ended
export function useBypassAmountPageExperimentVariation():
  | Variation
  | undefined {
  const { auth } = useAuthenticateUser();
  const { pageType } = useNotNullableContext(PageMetaContext);
  const isFundraisingPage = pageType.toUpperCase() === PageType.Fundraising;

  const isUserLoggedIn = auth?.isUserLoggedIn() || auth?.isGuest();
  const shouldSkipTest =
    typeof window === 'undefined' || isUserLoggedIn || !isFundraisingPage;

  const { data } = useFullPageQuery<BypassAmountPageEnrolmentQuery>(DOCUMENT, {
    variables: {
      isFundraisingPage,
    },
    skip: shouldSkipTest,
  });

  const shouldSkipEnrolment: boolean = (() => {
    if (
      shouldSkipTest ||
      !data?.page ||
      !data?.page.relationships?.beneficiaries.nodes[0] ||
      !data.page.donationSummary?.totalAmount?.currencyCode
    )
      return true;

    const {
      donationSummary: {
        totalAmount: { currencyCode },
      },
      relationships: {
        beneficiaries: { nodes, totalCount },
      },
    } = data.page;
    const charities = filterByTypename(nodes, 'Charity');

    return (
      currencyCode !== 'GBP' ||
      totalCount !== 1 ||
      !charities[0] ||
      charities[0].countryCode !== 'GB'
    );
  })();

  const isMobileDevice =
    typeof window !== 'undefined' ? isMobile(window) : false;
  const { enrolledIn } = useEnrolUser(
    isMobileDevice
      ? Experiments.starting_donation_on_frp_with_amount_page_mobile
      : Experiments.starting_donation_on_frp_with_amount_page_desktop,
    shouldSkipEnrolment,
  );

  return enrolledIn;
}
